import React, { useMemo, useState } from 'react';
import { Repeat, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import apiClient from '../../utils/apiClient';
import StripeTrustBadge from '../StripeTrustBadge';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

/**
 * Operator autopay checkboxes (Billing tab, owner ask 2026-08-02): one
 * checkbox per active rental.
 *
 * Enabling requires a saved card. When there is none, the operator can
 * set one up right here on the customer's behalf (owner ask 2026-08-03 —
 * "if customer asks us to set it up"): a SetupIntent vaults the card via
 * Stripe Elements (PAN goes browser -> Stripe direct), and the
 * setup_intent.succeeded webhook flips autopay on the customer + active
 * rentals — the same finalizer the tenant portal uses. Unchecking never
 * forgets the saved card; only the tenant's own portal disable does that
 * (consent withdrawal).
 */

const SetupCardForm = ({ onSaved, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    if (confirmError) {
      setError(confirmError.message || 'Could not save the card. Please try again.');
      setSubmitting(false);
      return;
    }
    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <PaymentElement options={{ paymentMethodOrder: ['card'] }} />
      <StripeTrustBadge size="standard" />
      {error && <p className="text-[13px] text-red-500">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Saving…' : 'Save card & enable autopay'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="text-[13px] text-slate-500 hover:text-slate-700 px-2 py-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const AutopaySection = ({
  facilityId, customerId, rentals, hasSavedMethod, onChanged,
}) => {
  const [busyId, setBusyId] = useState(null);
  const [local, setLocal] = useState({}); // rental id -> latest toggled value
  const [setup, setSetup] = useState(null); // {clientSecret, connectedAccount, publishableKey}
  const [setupStarting, setSetupStarting] = useState(false);
  const [cardJustSaved, setCardJustSaved] = useState(false);

  // Same dual-path init as PortalAutopay: a facility with its OWN
  // publishable key gets standalone Stripe.js; otherwise the legacy
  // shared platform key + {stripeAccount}.
  const stripePromise = useMemo(() => {
    if (!setup) return null;
    if (setup.publishableKey) return loadStripe(setup.publishableKey);
    if (!setup.connectedAccount || !PUBLISHABLE_KEY) return null;
    return loadStripe(PUBLISHABLE_KEY, { stripeAccount: setup.connectedAccount });
  }, [setup]);

  const active = (rentals || []).filter(
    (r) => ['Active', 'Delinquent'].includes(r.status));
  if (!active.length) return null;

  const idOf = (r) => r._id ?? r.id;
  const isOn = (r) => local[idOf(r)] ?? Boolean(r.autopay_enabled);

  const toggle = async (r) => {
    const rid = idOf(r);
    const next = !isOn(r);
    setBusyId(rid);
    try {
      await apiClient.patch(
        `/api/facilities/${facilityId}/customers/${customerId}/autopay`,
        { rental_id: rid, enabled: next },
      );
      setLocal((m) => ({ ...m, [rid]: next }));
      toast.success(next
        ? `Autopay on for unit ${r.unit_number || '—'} — the saved card is charged on the due date.`
        : `Autopay off for unit ${r.unit_number || '—'}.`);
      if (typeof onChanged === 'function') onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change autopay.');
    } finally {
      setBusyId(null);
    }
  };

  const startSetup = async () => {
    setSetupStarting(true);
    try {
      const res = await apiClient.post(
        `/api/facilities/${facilityId}/operator-payments/autopay/setup`,
        { customer_id: customerId },
      );
      setSetup({
        clientSecret: res.data.client_secret,
        connectedAccount: res.data.connected_account,
        publishableKey: res.data.publishable_key || null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start card setup.');
    } finally {
      setSetupStarting(false);
    }
  };

  const onCardSaved = () => {
    setSetup(null);
    setCardJustSaved(true);
    toast.success('Card saved — autopay is activating now.');
    // The setup_intent.succeeded webhook flips autopay server-side within
    // a few seconds; refresh the customer twice to pick it up.
    if (typeof onChanged === 'function') {
      setTimeout(onChanged, 2000);
      setTimeout(onChanged, 6000);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
        <Repeat className="h-4 w-4 text-slate-500" />
        <h3 className="text-[13px] font-semibold text-slate-700">Autopay</h3>
        {!setup && !cardJustSaved && (
          <div className="ml-auto flex items-center gap-3">
            {!hasSavedMethod && (
              <span className="text-[12px] text-amber-600">
                No saved card on file.
              </span>
            )}
            <button
              type="button"
              onClick={startSetup}
              disabled={setupStarting}
              className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-[12px] font-medium px-3 py-1.5 hover:bg-blue-100 disabled:opacity-50 transition-colors"
            >
              <CreditCard className="h-3.5 w-3.5" />
              {setupStarting ? 'Starting…'
                : hasSavedMethod ? 'Update card' : 'Set up card'}
            </button>
          </div>
        )}
        {cardJustSaved && !hasSavedMethod && (
          <span className="ml-auto text-[12px] text-emerald-600">
            Card saved — autopay activates in a few seconds.
          </span>
        )}
        {cardJustSaved && hasSavedMethod && (
          <span className="ml-auto text-[12px] text-emerald-600">
            New card saved — it replaces the previous one for autopay.
          </span>
        )}
      </div>
      {setup && (
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
          <p className="text-[12px] text-slate-500 mb-3">
            Enter the card the customer reads to you — it goes directly to
            Stripe and is saved for autopay on all their active units.
          </p>
          {!stripePromise ? (
            <p className="text-[13px] text-amber-600">
              The card form could not load — online payments are not fully
              configured for this facility.
            </p>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: setup.clientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <SetupCardForm onSaved={onCardSaved} onCancel={() => setSetup(null)} />
            </Elements>
          )}
        </div>
      )}
      <div className="px-5 py-3 space-y-2">
        {active.map((r) => {
          const rid = idOf(r);
          const on = isOn(r);
          const locked = !hasSavedMethod && !on;
          return (
            <label
              key={rid}
              className={`flex items-center gap-3 text-[13px] ${
                locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={busyId === rid || locked}
                onChange={() => toggle(r)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/30"
              />
              <span className="font-medium text-slate-700">
                Unit {r.unit_number || '—'}
              </span>
              <span className="text-slate-400">
                ${Number(r.monthly_rate || 0).toFixed(0)}/mo
              </span>
              <span className={`ml-auto text-[12px] ${
                on ? 'text-emerald-600' : 'text-slate-400'}`}>
                {on ? 'On — charged on the due date' : 'Off'}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default AutopaySection;
